import { useMemo } from "react";
import { classNames } from "primereact/utils";
import styles from "./DesignStudioMock.module.scss";
import { IDesignStudioColors } from "../models/designStudioPresetModel";
import {
  buildDesignStudioPreviewStyle,
  getDesignStudioPaletteName,
} from "../utils/designStudioPreviewUtility";

interface IDesignStudioMockProps {
  colors: IDesignStudioColors;
  variant: "card" | "full";
  studioName?: string;
  className?: string;
}

type SlotState = "free" | "busy" | "out" | "selected";

const CARD_DAYS = [19, 20, 21, 22, 23, 24, 25];
const CARD_SELECTED_DAY = 21;
const CARD_SLOTS: { time: string; state: SlotState }[] = [
  { time: "09:00", state: "free" },
  { time: "10:00", state: "selected" },
  { time: "11:00", state: "busy" },
  { time: "12:00", state: "free" },
];

const NAV_ITEMS = ["Календар", "Клиенти", "Абонаменти", "Каса", "Статистика"];
const EMPLOYEES = [
  { initials: "МП", name: "Мария" },
  { initials: "ИГ", name: "Иван" },
  { initials: "ЕД", name: "Елена" },
];
const WEEKDAYS = ["П", "В", "С", "Ч", "П", "С", "Н"];
// October 2026 starts on a Thursday; the preview "today" is the 19th, the booking the 21st.
const MONTH_OFFSET = 3;
const MONTH_DAYS = 31;
const TODAY = 19;
const SELECTED_DAY = 21;
const BUSY_DAYS = [20, 23, 27];
const FULL_SLOTS: { time: string; state: SlotState }[] = [
  { time: "09:00", state: "free" },
  { time: "09:30", state: "busy" },
  { time: "10:00", state: "selected" },
  { time: "10:30", state: "free" },
  { time: "11:00", state: "free" },
  { time: "11:30", state: "busy" },
  { time: "12:00", state: "out" },
  { time: "12:30", state: "out" },
];

const slotClass = (state: SlotState) =>
  classNames(styles.slot, {
    [styles.slot_free]: state === "free",
    [styles.slot_busy]: state === "busy",
    [styles.slot_out]: state === "out",
    [styles.slot_selected]: state === "selected",
  });

const CheckGlyph = () => (
  <svg viewBox="0 0 12 12" className={styles.glyph}>
    <path d="M2.5 6.4 5 8.8 9.6 3.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CalendarGlyph = () => (
  <svg viewBox="0 0 16 16" className={styles.glyph}>
    <rect x="2" y="3.5" width="12" height="10.5" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <path d="M2 7h12M5.5 2v3M10.5 2v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const ChevronGlyph = ({ flip }: { flip?: boolean }) => (
  <svg viewBox="0 0 12 12" className={classNames(styles.glyph, { [styles.glyph_flip]: flip })}>
    <path d="M7.5 2.5 4 6l3.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CardMock = () => (
  <div className={styles.card}>
    <div className={styles.card_sidebar}>
      <span className={styles.card_brand} />
      {[0, 1, 2, 3].map((index) => (
        <span
          key={index}
          className={classNames(styles.card_nav, { [styles.card_nav_active]: index === 0 })}
        />
      ))}
    </div>

    <div className={styles.card_stage}>
      <div className={styles.card_dialog}>
        <div className={styles.card_title}>Запис на час</div>

        <div className={styles.card_days}>
          {CARD_DAYS.map((day) => (
            <span
              key={day}
              className={classNames(styles.card_day, {
                [styles.card_day_selected]: day === CARD_SELECTED_DAY,
              })}
            >
              {day}
            </span>
          ))}
        </div>

        <div className={styles.card_slots}>
          {CARD_SLOTS.map((slot) => (
            <span key={slot.time} className={slotClass(slot.state)}>
              {slot.time}
            </span>
          ))}
        </div>

        <div className={styles.card_controls}>
          <span className={styles.control}>
            <span className={classNames(styles.switch, styles.switch_on)} />
            SMS
          </span>
          <span className={styles.control}>
            <span className={classNames(styles.checkbox, styles.checkbox_on)}>
              <CheckGlyph />
            </span>
            Платено
          </span>
        </div>

        <div className={styles.card_actions}>
          <span className={styles.button}>Запази</span>
          <span className={styles.cta}>Потвърди</span>
        </div>
      </div>
    </div>
  </div>
);

const FullMock = ({ studioName }: { studioName: string }) => {
  const days = useMemo(
    () => [
      ...Array.from({ length: MONTH_OFFSET }, () => null),
      ...Array.from({ length: MONTH_DAYS }, (_, index) => index + 1),
    ],
    [],
  );

  return (
    <div className={styles.full}>
      <aside className={styles.sidebar}>
        <div className={styles.brand}>
          <span className={styles.brand_logo}>{studioName.charAt(0).toUpperCase()}</span>
          <span className={styles.brand_name}>{studioName}</span>
        </div>

        <div className={styles.nav}>
          {NAV_ITEMS.map((item, index) => (
            <span
              key={item}
              className={classNames(styles.nav_item, { [styles.nav_item_active]: index === 0 })}
            >
              <span className={styles.nav_icon} />
              {item}
            </span>
          ))}
        </div>
      </aside>

      <div className={styles.main}>
        <div className={styles.topbar}>
          <span className={styles.topbar_title}>Календар</span>
          <span className={styles.topbar_actions}>
            <span className={styles.ghost}>Филтри</span>
            <span className={styles.button}>Запази</span>
          </span>
        </div>

        <div className={styles.dialog}>
          <div className={styles.dialog_header}>
            <span className={styles.dialog_title}>Запис на час</span>
            <span className={styles.dialog_subtitle}>Изберете служител, дата и час</span>

            <span className={styles.chips}>
              {EMPLOYEES.map((employee, index) => (
                <span
                  key={employee.name}
                  className={classNames(styles.chip, { [styles.chip_active]: index === 0 })}
                >
                  <span className={styles.chip_avatar}>{employee.initials}</span>
                  {employee.name}
                </span>
              ))}
            </span>
          </div>

          <div className={styles.dialog_body}>
            <div className={styles.calendar}>
              <div className={styles.calendar_nav}>
                <span className={styles.nav_button}>
                  <ChevronGlyph />
                </span>
                <span className={styles.calendar_month}>Октомври 2026</span>
                <span className={styles.nav_button}>
                  <ChevronGlyph flip />
                </span>
              </div>

              <div className={styles.calendar_grid}>
                {WEEKDAYS.map((weekday, index) => (
                  <span key={`w${index}`} className={styles.weekday}>
                    {weekday}
                  </span>
                ))}
                {days.map((day, index) =>
                  day === null ? (
                    <span key={`e${index}`} className={styles.day_blank} />
                  ) : (
                    <span
                      key={day}
                      className={classNames(styles.day, {
                        [styles.day_past]: day < TODAY,
                        [styles.day_open]: day >= TODAY,
                        [styles.day_today]: day === TODAY,
                        [styles.day_selected]: day === SELECTED_DAY,
                      })}
                    >
                      {day}
                      {day > TODAY && day !== SELECTED_DAY && (
                        <span
                          className={classNames(styles.dot, {
                            [styles.dot_busy]: BUSY_DAYS.includes(day),
                          })}
                        />
                      )}
                    </span>
                  ),
                )}
              </div>
            </div>

            <div className={styles.slots}>
              <div className={styles.slots_header}>
                <span className={styles.slots_title}>Свободни часове</span>
                <span className={styles.slots_date}>сряда, 21 октомври</span>
              </div>

              <span className={styles.slots_group}>Сутрин</span>
              <div className={styles.slots_grid}>
                {FULL_SLOTS.map((slot) => (
                  <span key={slot.time} className={slotClass(slot.state)}>
                    {slot.time}
                  </span>
                ))}
              </div>

              <div className={styles.options}>
                <span className={styles.control}>
                  <span className={classNames(styles.switch, styles.switch_on)} />
                  Напомняне по SMS
                </span>
                <span className={styles.control}>
                  <span className={styles.switch} />
                  Повтаряща се
                </span>
                <span className={styles.control}>
                  <span className={classNames(styles.checkbox, styles.checkbox_on)}>
                    <CheckGlyph />
                  </span>
                  Платено
                </span>
                <span className={styles.date_field}>
                  21.10.2026
                  <span className={styles.date_icon}>
                    <CalendarGlyph />
                  </span>
                </span>
              </div>
            </div>
          </div>

          <div className={styles.dialog_footer}>
            <span className={styles.summary}>
              <b>Мария</b>
              <span className={styles.summary_separator}>·</span>
              21 окт
              <span className={styles.summary_separator}>·</span>
              10:00
            </span>
            <span className={styles.footer_actions}>
              <span className={styles.back}>Назад</span>
              <span className={styles.cta}>Потвърди</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * A miniature of Planny painted with a palette. Every colour is a custom property on the
 * mock's own root, so a preview never touches the theme of the page around it.
 */
export const DesignStudioMock = ({
  colors,
  variant,
  studioName,
  className,
}: IDesignStudioMockProps) => {
  const style = useMemo(() => buildDesignStudioPreviewStyle(colors), [colors]);
  const isFull = variant === "full";

  return (
    <div
      className={classNames(styles.mock, isFull ? styles.mock_full : styles.mock_card, className)}
      style={style}
      data-variant={variant}
      {...(isFull
        ? {
            role: "img",
            "aria-label": `Преглед на палитра „${getDesignStudioPaletteName(colors)}“`,
          }
        : { "aria-hidden": true })}
    >
      {isFull ? <FullMock studioName={studioName || "Вашето студио"} /> : <CardMock />}
    </div>
  );
};
